/**
 * What a subscription state means, in one place and with no JSX.
 *
 * The overview and the billing screen both have to describe the same state,
 * and §7.3's failure mode is the two of them describing it differently — one
 * says "lapsed", the other implies the data is gone. So both read from here.
 *
 * Kept free of components for two reasons. It lives in its own module rather
 * than in one of the screens because the screens would otherwise import from
 * each other, and a cycle between two pages works until a bundler orders them
 * the other way. And it returns data rather than markup because *what an owner
 * is told* is the part worth testing, and a test of it should not have to
 * render anything.
 */

import { date } from './format.js'
import type { Subscription } from './api.js'

export type Tone = 'ok' | 'warn' | 'error' | 'muted'

/** The status word an owner sees, so the pages cannot disagree. */
export function statusLabel(subscription: Subscription): { text: string; tone: Tone } {
  if (subscription.status === 'active') return { text: 'Active', tone: 'ok' }
  if (subscription.status === 'trial') {
    const days = subscription.trialDaysRemaining ?? 0
    return { text: `Free trial — ${days} day${days === 1 ? '' : 's'} left`, tone: 'ok' }
  }
  if (subscription.status === 'lapsed') return { text: 'Lapsed', tone: 'error' }
  return { text: 'Not paid yet', tone: 'warn' }
}

/**
 * What this state stops, and what it leaves alone.
 *
 * Read off `capabilities` — the server's own answer — rather than off the
 * status word, so a future tier or entitlement that allows something new is
 * reflected without anyone remembering to update a list here.
 *
 * Both halves are returned because §16.16 has both, and the "still working"
 * half is the one that gets dropped: an owner whose till has stopped needs to
 * know their sales are safe as urgently as they need to know why it stopped.
 */
export function consequencesOf(subscription: Subscription): {
  blocked: string[]
  working: string[]
} {
  const c = subscription.capabilities
  const blocked: string[] = []
  const working: string[] = []

  ;(c.canDownloadInstaller ? working : blocked).push('Downloading the till software')
  ;(c.canEnrolDevice ? working : blocked).push('Setting up a new device')

  if (subscription.status === 'active' || subscription.status === 'trial') {
    working.push('Selling on the till')
  } else {
    // Not a capability flag: this one is enforced on the device by the licence
    // rather than by an endpoint here (§7.6, §16.16), and the grace period is
    // why the wording is "once it runs out" rather than "now".
    blocked.push('Selling on the till, once the grace period runs out')
  }

  if (c.canSync) working.push('Backing up your sales to this account')
  if (c.canReleaseDevice) working.push('Releasing a lost or stolen device')
  working.push('Signing in here and seeing your records')

  return { blocked, working }
}

/** The sentence explaining *why*, which is the half a status word leaves out. */
export function whyBlocked(subscription: Subscription): string | null {
  if (subscription.status === 'unpaid') {
    return 'Nothing has been paid on this account yet, so the till software has not been released to you.'
  }
  if (subscription.status === 'lapsed') {
    return `Cover ran out on ${date(subscription.paidThrough)}. Paying for a month restores everything immediately — nothing has been deleted.`
  }
  return null
}
